import React, { useEffect, useState } from 'react';
import { AnalyticsEvent } from '../analytics';

import {
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import { FlexLayout, SidebarMapLayout } from '../layouts/Layouts';
import { RequireAuth } from '../user/RequireAuth';
import { DayOverDay } from '../adhoc/DayOverDay';
import { Export } from '../exports/Export';
import { MarketData } from '../flows/MarketData';
import { Scenarios } from '../scenarios/Scenarios';
import { ScenarioGroups } from '../scenarios/ScenarioGroups';
import { ScenarioGroup } from '../scenarios/ScenarioGroup';
import { RenderWithKey, ScenarioPage } from '../scenarios/ScenarioPage';
import { AllPaths } from '../modes/AllPaths';
import { AllSources } from '../modes/AllSources';
import { Segmentation } from '../modes/Segmentation';
import { PathReport } from '../modes/PathReport';
import { PathFinder } from '../pathBuilder/PathFinder';
import { PathBuilder } from '../pathBuilder/PathBuilder';
import { Optimize } from '../modes/Optimize';
import DataViz from '../modes/DataViz';
import { Contracts } from '../contracts/Contracts';
import { IOC } from '../ioc/IOC';
import { ContractWrapper } from '../contracts/Contract';
import { Can } from '../user/Can';
import { MapBuilder } from '../modes/MapBuilder';
import { Login } from '../user/Login';
import { AccountSettingsPage } from '../user/AccountSettingsPage';
import { useViewMode } from '../hooks/useViewMode';
import { MaintenancePage } from '../maintenance/MaintenancePage';
import { KatanaExplorer } from '../modes/KatanaExplorer';
import { FlowDashboard } from '../flows/FlowDashboard';
import { DashboardsPage } from '../flows/DashboardsPage';
import { useAnalytics } from '../hooks/useAnalytics';

const LegacyDashboardRedirect = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  return <Navigate to={`/dashboards/${dashboardId || '1'}`} replace />;
};

export const AppRoutes = () => {
  const [navContent, _setNavContent] = useState<React.ReactNode>(<></>);
  const [viewMode] = useViewMode();
  const location = useLocation();
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.logEvent(AnalyticsEvent.NAVIGATION, {
      location: location.pathname,
    });
  }, [location.pathname]);

  return (
    <Routes>
      <Route
        element={<FlexLayout navContent={navContent} heightCss={'vh-100'} />}
      >
        <Route element={<RequireAuth />}>
          {/* // ------------ AUTHENTICATION REQUIRED --------------- */}
          <Route path="/day-over-day" element={<DayOverDay />} />
          <Route path="/exports" element={<Export />} />
          <Route path="/account" element={<AccountSettingsPage />} />
          <Route path="/dashboards" element={<DashboardsPage />} />
          <Route path="/dashboards/:dashboardId" element={<FlowDashboard />} />
          <Route
            path="/dashboard"
            element={<Navigate to="/dashboards" replace />}
          />
          <Route
            path="/dashboard/:dashboardId"
            element={<LegacyDashboardRedirect />}
          />
          <Route
            path="/flow-dashboard"
            element={<Navigate to="/dashboards" replace />}
          />
          <Route
            path="/flow-dashboard/:dashboardId"
            element={<LegacyDashboardRedirect />}
          />
          {/* map */}
          <Route element={<SidebarMapLayout />}>
            <Route path="/" element={<></>} />
            <Route path="/market-conditions" element={<MarketData />} />
            <Route path="/explore-map" element={<></>} />
            <Route path="/scenarios" element={<Scenarios />} />
            {viewMode === 'development' && (
              <Route path="/scenarios/groups" element={<ScenarioGroups />} />
            )}
            {viewMode === 'development' && (
              <Route path="/scenarios/groups/:id" element={<ScenarioGroup />} />
            )}
            {viewMode === 'development' && (
              <Route
                path="/scenarios/groups/:scenarioGroupId/scenarios/:id"
                element={
                  <RenderWithKey>
                    {({ key, id, ...rest }) => (
                      <ScenarioPage key={key} scenarioId={id} {...rest} />
                    )}
                  </RenderWithKey>
                }
              />
            )}
            {/* <Route
              path="/scenarios/:id"
              element={
                <RenderWithKey>
                  {({ key, id }) => <ScenarioPage key={key} scenarioId={id} />}
                </RenderWithKey>
              }
            /> */}
            <Route
              path="/scenarios/:id/*"
              element={
                <RenderWithKey>
                  {({ key, id }) => <ScenarioPage key={key} scenarioId={id} />}
                </RenderWithKey>
              }
            />
            <Route path="/all-paths" element={<AllPaths />} />
            <Route path="/all-receipts" element={<AllSources />} />
            <Route path="/segmentation" element={<Segmentation />} />
            <Route path="/paths" element={<PathReport />}>
              <Route index element={<Navigate to="/paths/report" replace />} />
              <Route path="report" element={<PathReport />} />
              <Route
                path="finder"
                element={<PathFinder showControls={true} showTable={false} />}
              />
              <Route
                path="builder"
                element={<PathBuilder showControls={true} showTable={false} />}
              />
            </Route>
            <Route path="/optimize" element={<Optimize />} />
            <Route path="/visualize-data" element={<DataViz />} />
            <Route path="/contracts" element={<Contracts />} />
            <Route path="/index-of-customers" element={<IOC />} />
            <Route path="/notices" element={<MaintenancePage />} />
            <Route path="/katana-explorer" element={<KatanaExplorer />} />
            <Route
              path="/contracts/:id"
              element={<ContractWrapper isEditable detailed />}
            />
            <Route
              element={
                <Can
                  type="route"
                  can={[
                    'change_point',
                    'change_edge',
                    'change_interconnect',
                    'change_node',
                  ]}
                />
              }
            >
              <Route path="/map-builder" element={<MapBuilder />} />
            </Route>
          </Route>
          {/* // ------------ END AUTHENTICATION REQUIRED --------------- */}
        </Route>
        <Route path="/login" element={<Login />} />
      </Route>
    </Routes>
  );
};
